import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  BackArrow,
  Button,
  Input,
  Toggle,
  TopProgress,
  Typography,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { projectService } from "../../../../api";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import { usePageTitle } from "../../../../context/navigationContext";
import {
  Container,
  Header,
  FormSection,
  FormActions,
  FormRow,
} from "./styles";

const ProjectCreate: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  usePageTitle(t("CREATE_PROJECT"));

  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [createLoading, setCreateLoading] = useState<boolean>(false);

  const validationSchema = yup.object().shape({
    name: yup.string().required(t("REQUIRED_FIELD")),
    description: yup.string().required(t("REQUIRED_FIELD")),
    disabled: yup.boolean(),
  });

  const formik = useFormik({
    enableReinitialize: false,
    validationSchema,
    initialValues: {
      name: "",
      description: "",
      disabled: false,
    },
    onSubmit: (formValues) => {
      setCreateLoading(true);
      setPageLoading(true);

      projectService
        .createProject(formValues)
        .then(() => {
          viewAlert({
            severity: "information",
            message: t("PROJECT_CREATED_SUCCESSFULLY"),
          });
          navigate("/openstack/projects");
        })
        .catch((error) => {
          const backendErrors = error?.response?.data;
          if (backendErrors?.detail) {
            viewAlert({
              severity: "error",
              message: backendErrors.detail,
            });
          } else if (backendErrors) {
            setErrors(backendErrors);
          } else {
            viewAlert({
              severity: "error",
              message: t("FAILED_TO_CREATE_PROJECT"),
            });
          }
        })
        .finally(() => {
          setCreateLoading(false);
          setPageLoading(false);
        });
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setErrors,
  } = formik;

  const onFormSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      formik.submitForm();
    },
    [formik]
  );

  const formikProps = {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
  };

  return (
    <Container>
      <TopProgress loading={pageLoading} />
      <Header>
        <BackArrow fallbackPath="/openstack/projects" />
        <Typography variant="h1">{t("CREATE_PROJECT")}</Typography>
      </Header>
      <FormSection as="form" onSubmit={onFormSubmit}>
        <FormRow>
          <Input
            required
            title={t("PROJECT_NAME")}
            placeholder={t("ENTER_PROJECT_NAME")}
            fieldName="name"
            {...formikProps}
          />
        </FormRow>

        <FormRow>
          <Input
            required
            title={t("DESCRIPTION")}
            placeholder={t("ENTER_DESCRIPTION")}
            fieldName="description"
            isTextArea
            {...formikProps}
          />
        </FormRow>

        <FormRow>
          <Toggle
            value={values.disabled}
            onChange={() => setFieldValue("disabled", !values.disabled)}
            title={t("DISABLED")}
          />
        </FormRow>

        <FormActions>
          <Button
            variant="stroke"
            size="display"
            title={t("CANCEL")}
            onClick={() => navigate("/openstack/projects")}
          />
          <Button
            variant="primary"
            size="display"
            title={t("CREATE")}
            loading={createLoading}
            onClick={onFormSubmit}
          />
        </FormActions>
      </FormSection>
    </Container>
  );
};

export default ProjectCreate;
